import { ref, watch, computed } from 'vue';
import router from '@/common/router';
import { defineStore, storeToRefs } from 'pinia';
import { useDashboardViewStore } from '@/dashboard/stores/dashboard-view';
import { useStandardVariablesStore } from '@/dashboard/stores/global-variables/standard-variables';
import { useTreeVariablesStore } from '@/dashboard/stores/global-variables/tree-variables';
import { useGlobalFilterStore } from '@/dashboard/stores/global-filter';
import { CUSTOM_KEY } from '@/dashboard/features/widgets/common/constants/define';
import { useDashboardEditUserEnvStore } from '@/dashboard/stores/dashboard-edit-user-env';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { useDashboardTargetsColorSyncStore } from '@/dashboard/stores/dashboard-targets-color-sync';
import { getAllRequestTagsRecursively } from '@/dashboard/features/widgets/common/domain/requestTags';
import {
  useDashboardTargetsStore,
  type AllDashboardTargetsColumns,
} from '@/dashboard/stores/dashboard-targets';
import { convertToGridLayoutItemList } from '@/dashboard/features/dashboardCore/services/widgets';
import type { GridLayoutItem, WidgetModeType } from '@/dashboard/shared/types/widgets.types';
import { flattenWidgets } from '@/dashboard/features/dashboardLayout/domain/gridLayoutItem';
import { LEGEND_DISPLAY_STYLE } from '@/dashboard/features/dashboardLegend/domain/dashboardLegend.constants';
import type {
  TargetId,
  WidgetId,
  GlobalFilterId,
  MonitoringDashboardTargetList,
  MonitoringDashboardTargetListArray,
  TargetItem,
  LegendDisplayStyle,
  SelectedLegends,
  LegendSaveInfo,
  TargetChild,
} from '@/dashboard/features/dashboardLegend/domain/dashboardLegend.types';
import {
  filterDashboardTargetsColumnsByPreset,
  filterTargetListChildren,
  getWidgetDisplayName,
  addOrUpdateTargetToMap,
  shouldApplyPresetLegendTargetFilter,
} from '@/dashboard/features/dashboardLegend/domain/dashboardLegend.utils';
import { useDashboardUserEnvStore } from './dashboard-user-env';

const useLegendUserEnv = () => {
  const { getEnvValue, setEnvValue } = useDashboardUserEnvStore();
  const { getEditingUserEnvData, setEditingUserEnvData } = useDashboardEditUserEnvStore();

  const userEnvKeyGlobal = computed(
    () =>
      `${USER_ENV_KEYS.LEGEND_dashboard_global}_${
        router.currentRoute.value.params.id || router.currentRoute.value.query.id
      }`,
  );

  const userEnvKeyCustom = computed(
    () =>
      `${USER_ENV_KEYS.LEGEND_dashboard_user}_${
        router.currentRoute.value.params.id || router.currentRoute.value.query.id
      }`,
  );

  /**
   * 과거 코드에서 `monitoringDashboardTargetList`가 `legendTargetList`로 잘못 저장된
   * env 데이터가 존재할 수 있다. 읽기 시점에 제거하여 다음 저장 때 자연스럽게 정리되도록 한다.
   */
  const stripLegacyKeys = (
    envData: (LegendSaveInfo & { legendTargetList?: unknown }) | null,
  ): LegendSaveInfo | null => {
    if (!envData || !('legendTargetList' in envData)) return envData;
    const { legendTargetList: _, ...rest } = envData;
    return rest as LegendSaveInfo;
  };

  const envDataForEditMode = computed<LegendSaveInfo | null>(() => {
    return stripLegacyKeys(
      getEditingUserEnvData(userEnvKeyGlobal.value, 'LEGEND') ??
        getEnvValue<LegendSaveInfo | null>(userEnvKeyGlobal.value) ??
        getEnvValue<LegendSaveInfo | null>(userEnvKeyCustom.value),
    );
  });

  const envDataForViewMode = computed<LegendSaveInfo | null>(() => {
    return stripLegacyKeys(
      getEnvValue<LegendSaveInfo | null>(userEnvKeyCustom.value) ??
        getEnvValue<LegendSaveInfo | null>(userEnvKeyGlobal.value),
    );
  });

  const envLegendTargetMap = computed(() => {
    const mode = router.currentRoute.value.meta.mode || 'edit';
    const envData = mode === 'view' ? envDataForViewMode.value : envDataForEditMode.value;
    return new Map(envData?.monitoringDashboardTargetList);
  });

  const getEnvData = (mode: WidgetModeType = router.currentRoute.value.meta.mode || 'edit') => {
    switch (mode) {
      case 'edit':
      case 'preview':
        return envDataForEditMode.value;
      case 'view':
      default:
        return envDataForViewMode.value;
    }
  };

  return {
    userEnvKeyGlobal,
    userEnvKeyCustom,
    getEnvData,
    setEnvValue,
    setEditingUserEnvData,
    envLegendTargetMap,
  };
};

export const useDashboardLegendStore = defineStore('dashboardLegend', () => {
  const {
    userEnvKeyGlobal,
    userEnvKeyCustom,
    getEnvData,
    setEnvValue,
    setEditingUserEnvData,
    envLegendTargetMap,
  } = useLegendUserEnv();

  const isDashboardLegendOpen = ref(false);
  const hasLegendInteraction = ref(false);
  const legendTargetList = ref<MonitoringDashboardTargetList>(new Map());
  const originalLegendTargetList = ref<MonitoringDashboardTargetList>(new Map());
  const isAllLegendsSelected = ref(true);
  /**
   * 대시보드 범례 선택 상태
   * - `[]` (빈 배열): 전체 선택 (모든 범례 표시)
   * - `null`: 전체 해제 (모든 범례 숨김)
   * - `[{targetId, category}, ...]`: 특정 범례만 선택
   *
   * @see setSelectedLegendsShowAll 전체 선택으로 변경
   * @see setSelectedLegendsShowNone 전체 해제로 변경
   */
  const selectedLegends = ref<SelectedLegends[] | null>([]);

  const setSelectedLegendsShowAll = () => {
    selectedLegends.value = [];
  };
  const setSelectedLegendsShowNone = () => {
    selectedLegends.value = null;
  };

  const legendDisplayStyle = ref<LegendDisplayStyle>(LEGEND_DISPLAY_STYLE.HORIZONTAL);
  const legendTheme = ref(CUSTOM_KEY);
  const isChangedLegend = ref(false);

  const allWidgetsByCurrentDashboard = ref<GridLayoutItem[]>([]);

  const dashboardTargetsStore = useDashboardTargetsStore();

  const dashboardViewStore = useDashboardViewStore();
  const { widgetsForMonitoringDashboard } = storeToRefs(dashboardViewStore);

  const { standardVariables } = storeToRefs(useStandardVariablesStore());
  const treeVariablesStore = useTreeVariablesStore();
  const { presetList, currentPreset } = storeToRefs(useGlobalFilterStore());

  const { setTargetIndexMap, getDashboardTargetColor } = useDashboardTargetsColorSyncStore();

  const mergeTargetListForSave = (
    currentList: MonitoringDashboardTargetListArray,
    envData: LegendSaveInfo | null,
  ): MonitoringDashboardTargetListArray => {
    if (!envData?.monitoringDashboardTargetList?.length) {
      return currentList;
    }

    const currentMap = new Map(currentList);
    const mergedMap = new Map(envData.monitoringDashboardTargetList);

    // origin 목록의 편집된 값 반영 (비활성 탭 위젯 타겟의 변경 사항 포함)
    originalLegendTargetList.value.forEach(({ alias, color }, targetId) => {
      if (mergedMap.has(targetId)) {
        mergedMap.set(targetId, { alias, color });
      }
    });

    // 현재 활성 타겟의 값으로 업데이트 (최우선)
    currentMap.forEach((value, targetId) => {
      mergedMap.set(targetId, value);
    });

    return Array.from(mergedMap.entries());
  };

  const legendSaveInfo = computed<LegendSaveInfo>(() => ({
    isDashboardLegendOpen: isDashboardLegendOpen.value,
    legendDisplayStyle: legendDisplayStyle.value,
    legendTheme: legendTheme.value,
    monitoringDashboardTargetList: Array.from(legendTargetList.value.entries()).map(
      ([targetId, { alias, color }]) => [
        targetId,
        {
          alias,
          color,
        },
      ],
    ),
    selectedLegends: selectedLegends.value,
  }));

  const setAllWidgetsByCurrentDashboard = (widgets: GridLayoutItem[]) => {
    allWidgetsByCurrentDashboard.value = widgets;
  };

  const setDashboardLegendUserEnv = async ({
    mode = 'edit',
    isOnlySelected = false,
  }: {
    mode?: WidgetModeType;
    isOnlySelected?: boolean;
  } = {}) => {
    const envData = getEnvData(mode);

    const envDataValue =
      isOnlySelected && envData
        ? {
            ...envData,
            selectedLegends: selectedLegends.value,
          }
        : {
            ...legendSaveInfo.value,
            monitoringDashboardTargetList: mergeTargetListForSave(
              legendSaveInfo.value.monitoringDashboardTargetList,
              envData,
            ),
          };

    if (mode === 'edit') {
      setEditingUserEnvData(userEnvKeyGlobal.value, {
        chartType: 'LEGEND',
        global: true,
        value: envDataValue,
      });
      setEditingUserEnvData(userEnvKeyCustom.value, {
        chartType: 'LEGEND',
        global: false,
        value: envDataValue,
      });
    } else if (mode === 'view') {
      await setEnvValue([{ key: userEnvKeyCustom.value, value: envDataValue }], false);
    }
  };

  const setLegendClick = (value: boolean) => {
    hasLegendInteraction.value = value;
  };

  const removeTargetListFromWidgetId = (
    targetList: MonitoringDashboardTargetList,
    widgetIdToRemove: WidgetId,
  ) => {
    filterTargetListChildren(
      targetList,
      ([widgetId, { isInViewport }]) => widgetId !== widgetIdToRemove && !!isInViewport,
    );
  };

  const removeObsoleteWidgetsByTargetItems = (targetItems: TargetItem[] | WidgetId) => {
    if (Array.isArray(targetItems)) {
      const targetItemsByTargetId = new Map<TargetId, TargetItem>();
      const targetItemsByWidgetId = new Map<WidgetId, TargetItem>();
      const targetItemsByGlobalFilterId = new Map<GlobalFilterId, TargetItem[]>();

      targetItems.forEach((item) => {
        targetItemsByTargetId.set(item.targetId, item);
        targetItemsByWidgetId.set(item.widgetId, item);
        if (item.globalFilterId !== undefined) {
          const items = targetItemsByGlobalFilterId.get(item.globalFilterId) || [];
          items.push(item);
          targetItemsByGlobalFilterId.set(item.globalFilterId, items);
        }
      });

      legendTargetList.value.forEach((target, targetId) => {
        if (targetItemsByTargetId.has(targetId)) return;

        target.children = target.children.filter(
          ([widgetId, { isInViewport }]) => !targetItemsByWidgetId.has(widgetId) && isInViewport,
        );

        if (target.children.length === 0) {
          legendTargetList.value.delete(targetId);
        }
      });

      originalLegendTargetList.value.forEach((target, targetId) => {
        if (targetItemsByTargetId.has(targetId)) return;

        const targetGlobalFilterItems = target.globalFilterIds.flatMap(
          (gfId) => targetItemsByGlobalFilterId.get(gfId) || [],
        );

        const isManagedByActiveGlobalVariable =
          targetGlobalFilterItems.length > 0 &&
          standardVariables.value.some(
            (gv) =>
              target.globalFilterIds.includes(gv.globalFilterId) ||
              gv.targets.some(({ tagId }) => tagId === targetId),
          );

        if (isManagedByActiveGlobalVariable) return;

        target.children = target.children.filter(
          ([widgetId]) => !targetItemsByWidgetId.has(widgetId),
        );

        if (target.children.length === 0) {
          originalLegendTargetList.value.delete(targetId);
        }
      });
    } else {
      const widgetIdToRemove = targetItems;
      removeTargetListFromWidgetId(legendTargetList.value, widgetIdToRemove);
      removeTargetListFromWidgetId(originalLegendTargetList.value, widgetIdToRemove);
    }
  };

  const addOrUpdateTarget = (
    targetList: MonitoringDashboardTargetList,
    {
      targetId,
      widgetId,
      category,
      widgetName,
      targetName,
      alias,
      color,
      originColor,
      globalFilterId,
      isInViewport,
    }: TargetItem,
  ) => {
    if (category === 'business_service' || category === 'business_service_group') return;

    const existingTarget = targetList.get(targetId);

    if (existingTarget) {
      existingTarget.children = existingTarget.children.map((child) => {
        if (child[0] === widgetId) {
          return [widgetId, { widgetName, targetName, isInViewport }];
        }
        return child;
      });
      // 동일 타겟이 여러 글로벌 변수에 포함될 수 있으므로 globalFilterId를 누적
      if (
        globalFilterId !== undefined &&
        !existingTarget.globalFilterIds.includes(globalFilterId)
      ) {
        existingTarget.globalFilterIds.push(globalFilterId);
      }
    } else {
      targetList.set(targetId, {
        targetName,
        color,
        alias,
        originColor,
        category,
        globalFilterIds: globalFilterId !== undefined ? [globalFilterId] : [],
        children: [[widgetId, { widgetName, targetName, isInViewport }]],
      });
    }
  };

  const addOrUpdateTargets = (targetItems: TargetItem[]) => {
    targetItems.forEach((item) => {
      addOrUpdateTarget(legendTargetList.value, item);
      addOrUpdateTarget(originalLegendTargetList.value, item);
    });
  };

  const sortTargetListByName = (
    targetList: MonitoringDashboardTargetList,
  ): MonitoringDashboardTargetList => {
    return new Map(
      [...targetList.entries()].sort((a, b) => a[1].targetName.localeCompare(b[1].targetName)),
    );
  };

  const sortLegendTargetLists = () => {
    legendTargetList.value = sortTargetListByName(legendTargetList.value);
    originalLegendTargetList.value = sortTargetListByName(originalLegendTargetList.value);
  };

  const applyUserEnvToTargetList = (legendList: MonitoringDashboardTargetList) => {
    const envData: LegendSaveInfo | null = getEnvData();
    const envDataMap = new Map(envData?.monitoringDashboardTargetList);

    const updatedLegendTargetList = new Map();
    legendList.forEach((originTarget, targetId) => {
      const currentTarget = envDataMap.get(targetId);
      if (currentTarget) {
        updatedLegendTargetList.set(targetId, {
          ...originTarget,
          color: currentTarget.color,
          alias: currentTarget.alias,
        });
      } else {
        updatedLegendTargetList.set(targetId, originTarget);
      }
    });

    return updatedLegendTargetList;
  };

  const syncLegendTargetList = (targetItems: TargetItem[] | WidgetId) => {
    legendTargetList.value = applyUserEnvToTargetList(legendTargetList.value);
    originalLegendTargetList.value = applyUserEnvToTargetList(originalLegendTargetList.value);

    removeObsoleteWidgetsByTargetItems(targetItems);

    if (Array.isArray(targetItems)) {
      addOrUpdateTargets(targetItems);
    }

    sortLegendTargetLists();
  };

  const getLegendTargetById = (targetId: string) => {
    return isDashboardLegendOpen.value ? envLegendTargetMap.value.get(targetId) : null;
  };

  /**
   * 범례 편집창에서 저장 시 사용.
   * originalLegendTargetList는 전체 갱신하고,
   * legendTargetList는 기존에 존재하는 타겟의 편집 가능한 속성(alias, color)만 머지한다.
   * 이를 통해 비활성 탭 위젯의 타겟이 범례에 노출되지 않도록 한다.
   */
  const replaceLegendTargetList = (editedData: MonitoringDashboardTargetList) => {
    originalLegendTargetList.value = sortTargetListByName(editedData);

    legendTargetList.value.forEach((target, targetId) => {
      const editedTarget = editedData.get(targetId);
      if (editedTarget) {
        target.alias = editedTarget.alias;
        target.color = editedTarget.color;
        target.originColor = editedTarget.originColor;
      }
    });
    legendTargetList.value = sortTargetListByName(legendTargetList.value);
  };

  const removeObsoleteWidgetsByWidgets = (widgets: GridLayoutItem[]) => {
    const filterFn = (child: TargetChild) => widgets.some((widget) => widget.i === child[0]);

    filterTargetListChildren(legendTargetList.value, filterFn);
    filterTargetListChildren(originalLegendTargetList.value, filterFn);
  };

  const setDefaultLegendValues = () => {
    isDashboardLegendOpen.value = false;
    legendDisplayStyle.value = LEGEND_DISPLAY_STYLE.HORIZONTAL;
    legendTheme.value = CUSTOM_KEY;
    setSelectedLegendsShowAll();
  };

  const applyLegendConfig = (
    mode: WidgetModeType = router.currentRoute.value.meta.mode || 'edit',
  ) => {
    const envData: LegendSaveInfo | null = getEnvData(mode);

    if (envData) {
      const {
        isDashboardLegendOpen: isOpen,
        legendDisplayStyle: style,
        legendTheme: theme,
        selectedLegends: selected = [],
      } = envData;
      isDashboardLegendOpen.value = isOpen;
      legendDisplayStyle.value = style;
      legendTheme.value = theme;
      if (selected === null || selected.length) {
        setLegendClick(true);
      }
      selectedLegends.value = selected;
    } else {
      setDefaultLegendValues();
    }
  };

  const buildLegendTargetListFromWidgets = async ({
    isInit = true,
    skipPresetTransform = false,
  }: { isInit?: boolean; skipPresetTransform?: boolean } = {}) => {
    if (!isDashboardLegendOpen.value) {
      return;
    }

    const envData = getEnvData();
    const shouldFilterTargetsByPreset = shouldApplyPresetLegendTargetFilter({
      mode: router.currentRoute.value.meta.mode as WidgetModeType | undefined,
      isDashboardLegendOpen: isDashboardLegendOpen.value,
      selectedLegends: envData?.selectedLegends,
    });

    const fetchOptions = skipPresetTransform ? { skipPresetTransform: true } : undefined;

    if (isInit) {
      let widgets: GridLayoutItem[] = [];
      if (allWidgetsByCurrentDashboard.value.length > 0) {
        widgets = allWidgetsByCurrentDashboard.value;
      } else if (widgetsForMonitoringDashboard.value?.widgets?.length) {
        widgets = convertToGridLayoutItemList(widgetsForMonitoringDashboard.value.widgets);
      }

      const filteredWidgets = widgets.filter(({ dataResource }) => dataResource === 'default');
      if (filteredWidgets.length === 0) return;

      const allWidgets = flattenWidgets(filteredWidgets);
      const tempAllRequestTags = getAllRequestTagsRecursively(filteredWidgets);

      setAllWidgetsByCurrentDashboard(allWidgets);
      await dashboardTargetsStore.initializeDashboardTargets(tempAllRequestTags, fetchOptions);
    } else {
      await dashboardTargetsStore.initializeDashboardTargets(undefined, fetchOptions);
    }

    const fetchedTargetsColumns = dashboardTargetsStore.allDashboardTargetsColumns ?? [];
    const data: AllDashboardTargetsColumns[] = shouldFilterTargetsByPreset
      ? filterDashboardTargetsColumnsByPreset({
          targetsColumns: fetchedTargetsColumns,
          presetList: presetList.value,
          currentPresetId: currentPreset.value.userFilterId,
          globalVariables: standardVariables.value,
          treeVariables: treeVariablesStore.treeVariables,
        })
      : fetchedTargetsColumns;

    dashboardTargetsStore.setAllDashboardTargetsColumns(data);

    const updatedLegendTargetList: MonitoringDashboardTargetList = new Map();

    let colorIndex = 0;
    data.forEach(({ widgetUUID, targets, isInViewport, globalFilterIds, treeVariableIds }) => {
      if (!widgetUUID) return;

      const matchedWidget = allWidgetsByCurrentDashboard.value?.find((widget) => {
        return widget.i === widgetUUID;
      });

      const widgetName = getWidgetDisplayName(matchedWidget);

      if (!targets) return;

      const targetsArr = targets.map(({ id, name }) => ({
        targetId: id || '',
        targetName: name || '',
      }));

      setTargetIndexMap(targetsArr);

      targets.forEach(({ id: targetId, name: targetName, ccategory, alias: aliasBySettings }) => {
        const existingEnvTarget = envLegendTargetMap.value.get(targetId || '');
        const originColor = getDashboardTargetColor({
          colorTheme: matchedWidget?.colorTheme || CUSTOM_KEY,
          targetId: targetId || '',
          colorIndex,
        });
        const color = existingEnvTarget?.color ?? originColor;
        const alias = existingEnvTarget?.alias || aliasBySettings || '';

        addOrUpdateTargetToMap(updatedLegendTargetList, targetId || '', {
          targetName: targetName || '',
          category: ccategory ?? '',
          children: [[widgetUUID, { widgetName, targetName: targetName || '', isInViewport }]],
          alias,
          color,
          originColor,
          globalFilterIds: [...(globalFilterIds ?? []), ...(treeVariableIds ?? [])],
        });

        colorIndex++;
      });
    });

    if (isInit) {
      legendTargetList.value = sortTargetListByName(updatedLegendTargetList);
    }
    originalLegendTargetList.value = sortTargetListByName(updatedLegendTargetList);
  };

  const clearLegendTargetList = () => {
    legendTargetList.value.clear();
  };

  const resetLegendState = ({ preserveWidgets = false }: { preserveWidgets?: boolean } = {}) => {
    dashboardTargetsStore.setAllDashboardTargetsColumns([]);
    if (!preserveWidgets) {
      setAllWidgetsByCurrentDashboard([]);
    }
    legendTargetList.value.clear();
    originalLegendTargetList.value.clear();
    setLegendClick(false);
  };

  const refreshLegendTargetList = async ({
    refreshMonitoring = false,
    skipPresetTransform = false,
  }: { refreshMonitoring?: boolean; skipPresetTransform?: boolean } = {}) => {
    await buildLegendTargetListFromWidgets({ isInit: false, skipPresetTransform });

    if (refreshMonitoring) {
      legendTargetList.value = sortTargetListByName(originalLegendTargetList.value);
    }
  };

  const initLegendStore = async () => {
    const preserveWidgets = [...allWidgetsByCurrentDashboard.value].length > 0;

    resetLegendState({ preserveWidgets });

    await buildLegendTargetListFromWidgets();

    applyLegendConfig();
  };

  const selectAllLegends = async (mode?: WidgetModeType) => {
    setSelectedLegendsShowAll();
    setLegendClick(false);
    if (mode) {
      await setDashboardLegendUserEnv({ mode, isOnlySelected: true });
    }
  };

  const clearSelectedLegends = async ({ mode }: { mode: WidgetModeType }) => {
    setSelectedLegendsShowNone();
    await setDashboardLegendUserEnv({ mode, isOnlySelected: true });
  };

  const toggleLegend = async ({
    targetId,
    category,
    mode,
  }: {
    targetId: TargetId;
    category?: string;
    mode?: WidgetModeType;
  }) => {
    if (!targetId) {
      setSelectedLegendsShowAll();
      setLegendClick(false);
      if (mode) {
        await setDashboardLegendUserEnv({ mode, isOnlySelected: true });
      }
      return;
    }

    if (selectedLegends.value === null) {
      setSelectedLegendsShowAll();
    }
    isAllLegendsSelected.value = false;

    const legends = selectedLegends.value ?? [];
    const isAlreadySelected = legends.some((legend) => legend.targetId === targetId);

    if (isAlreadySelected) {
      selectedLegends.value = legends.filter((legend) => legend.targetId !== targetId);
      if (selectedLegends.value.length === 0) {
        setSelectedLegendsShowNone();
      }
    } else {
      const newLegends = [...legends, { targetId, category }];
      if (newLegends.length === legendTargetList.value.size) {
        setSelectedLegendsShowAll();
        setLegendClick(false);
      } else {
        selectedLegends.value = newLegends;
      }
    }

    if (mode) {
      await setDashboardLegendUserEnv({ mode, isOnlySelected: true });
    }
  };

  const updateLegendDisplayStyle = (style: LegendDisplayStyle) => {
    legendDisplayStyle.value = style;
  };

  const updateLegendTheme = (theme: string) => {
    legendTheme.value = theme;
  };

  const updateChangedLegend = (value: boolean) => {
    isChangedLegend.value = value;
  };

  const setDashboardLegendOpen = (value: boolean) => {
    const envData: LegendSaveInfo | null = getEnvData();

    const envValue: LegendSaveInfo = {
      isDashboardLegendOpen: value,
      legendDisplayStyle: envData?.legendDisplayStyle ?? LEGEND_DISPLAY_STYLE.HORIZONTAL,
      legendTheme: envData?.legendTheme ?? CUSTOM_KEY,
      monitoringDashboardTargetList:
        (envData?.monitoringDashboardTargetList?.length
          ? envData?.monitoringDashboardTargetList
          : Array.from(legendTargetList.value)) ?? [],
      selectedLegends: [],
    };

    isDashboardLegendOpen.value = value;
    setEditingUserEnvData(userEnvKeyGlobal.value, {
      chartType: 'LEGEND',
      global: true,
      value: envValue,
    });
    setEditingUserEnvData(userEnvKeyCustom.value, {
      chartType: 'LEGEND',
      global: false,
      value: envValue,
    });
  };

  watch(
    () => selectedLegends.value,
    (selects) => {
      if (selects?.length === 0) {
        isAllLegendsSelected.value = true;
      } else {
        isAllLegendsSelected.value = false;
      }
    },
    {
      flush: 'post',
    },
  );

  return {
    allWidgetsByCurrentDashboard,
    setAllWidgetsByCurrentDashboard,

    isDashboardLegendOpen,
    hasLegendInteraction,
    setDashboardLegendOpen,
    setLegendClick,
    removeTargetListFromWidgetId,

    legendTargetList,
    originalLegendTargetList,
    applyUserEnvToTargetList,
    syncLegendTargetList,
    getLegendTargetById,
    replaceLegendTargetList,
    removeObsoleteWidgetsByWidgets,

    isAllLegendsSelected,
    selectedLegends,
    clearSelectedLegends,
    selectAllLegends,
    toggleLegend,

    legendDisplayStyle,
    updateLegendDisplayStyle,

    legendTheme,
    updateLegendTheme,

    isChangedLegend,
    updateChangedLegend,

    envLegendTargetMap,

    refreshLegendTargetList,

    clearLegendTargetList,
    resetLegendState,

    setDashboardLegendUserEnv,
    applyLegendConfig,

    initLegendStore,
  };
});
